import { supabase } from './supabaseClient';

const REG_META_PREFIX = 'ct_reg_meta:';

export const savePendingRegistration = (email, meta) => {
  try {
    if (!email) return;
    const key = REG_META_PREFIX + email.toLowerCase();
    localStorage.setItem(key, JSON.stringify(meta));
  } catch (e) {
    console.warn('Failed to save pending registration metadata', e);
  }
};

export const consumePendingRegistration = (email) => {
  try {
    if (!email) return null;
    const key = REG_META_PREFIX + email.toLowerCase();
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    localStorage.removeItem(key);
    return JSON.parse(raw);
  } catch (e) {
    console.warn('Failed to read pending registration metadata', e);
    return null;
  }
};

export const createOrUpdateProfile = async (overrides = {}) => {
  if (!supabase) throw new Error('Supabase not configured');

  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError) throw userError;
  const user = userData?.user;
  if (!user) throw new Error('No authenticated user');

  const userId = user.id;
  const email = user.email || overrides.email;

  // Try to load existing profile for this user
  const { data: existing, error: selectError } = await supabase
    .from('profiles')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (selectError && selectError.code !== 'PGRST116') {
    // PGRST116 = no rows; treat that as "no existing profile"
    throw selectError;
  }

  const baseData = {
    full_name: overrides.full_name,
    organization: overrides.organization,
    role: overrides.role,
    phone: overrides.phone,
    email,
    // Extended SPOC/uploader attributes (optional; require matching columns in Supabase)
    institution_type: overrides.institution_type,
    institution_website: overrides.institution_website,
    accreditation_number: overrides.accreditation_number,
    institution_address: overrides.institution_address,
    estimated_uploads: overrides.estimated_uploads,
    certificate_types: overrides.certificate_types,
    agreed_terms: overrides.agreed_terms,
    agreed_data_privacy: overrides.agreed_data_privacy,
    is_verified: true,
    user_id: userId
  };

  if (existing) {
    const updatePayload = {
      full_name: baseData.full_name || existing.full_name,
      organization: baseData.organization || existing.organization,
      role: baseData.role || existing.role,
      phone: baseData.phone || existing.phone,
      email: baseData.email || existing.email,
      institution_type: baseData.institution_type || existing.institution_type,
      institution_website: baseData.institution_website || existing.institution_website,
      accreditation_number: baseData.accreditation_number || existing.accreditation_number,
      institution_address: baseData.institution_address || existing.institution_address,
      estimated_uploads: baseData.estimated_uploads || existing.estimated_uploads,
      certificate_types: baseData.certificate_types || existing.certificate_types,
      agreed_terms: typeof baseData.agreed_terms === 'boolean' ? baseData.agreed_terms : existing.agreed_terms,
      agreed_data_privacy:
        typeof baseData.agreed_data_privacy === 'boolean'
          ? baseData.agreed_data_privacy
          : existing.agreed_data_privacy,
      is_verified: true
    };

    const { data, error } = await supabase
      .from('profiles')
      .update(updatePayload)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  const insertPayload = {
    user_id: userId,
    full_name: baseData.full_name || null,
    organization: baseData.organization || null,
    role: baseData.role || null,
    phone: baseData.phone || null,
    email: baseData.email || email,
    institution_type: baseData.institution_type || null,
    institution_website: baseData.institution_website || null,
    accreditation_number: baseData.accreditation_number || null,
    institution_address: baseData.institution_address || null,
    estimated_uploads: baseData.estimated_uploads || null,
    certificate_types: baseData.certificate_types || null,
    agreed_terms: typeof baseData.agreed_terms === 'boolean' ? baseData.agreed_terms : null,
    agreed_data_privacy:
      typeof baseData.agreed_data_privacy === 'boolean' ? baseData.agreed_data_privacy : null,
    is_verified: true
  };

  const { data, error } = await supabase
    .from('profiles')
    .insert([insertPayload])
    .select()
    .single();

  if (error) throw error;
  return data;
};
